import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Workspace {
  id: string
  name: string
  description: string
  owner: string
  members_count: number
  channels_count: number
  last_activity: string
  status: string
}

interface Task {
  id: string
  title: string
  description: string
  assignee: string
  reporter: string
  status: string
  priority: string
  due_date: string
  comments_count: number
}

export default function TeamCollaborationPage() {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [wsRes, taskRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/team-collaboration/workspaces'),
      fetch(API_BASE + '/team-collaboration/tasks'),
      fetch(API_BASE + '/team-collaboration/statistics')
    ])
    if (wsRes.ok) {
      const ws = (await wsRes.json()).workspaces || []
      setWorkspaces(ws)
      if (ws.length > 0) setSelectedWorkspace(ws[0])
    }
    if (taskRes.ok) setTasks((await taskRes.json()).tasks || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'in_review': return 'bg-purple-100 text-purple-800'
      case 'todo': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600'
      case 'high': return 'text-orange-600'
      case 'medium': return 'text-yellow-600'
      case 'low': return 'text-gray-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Team Collaboration Hub</h1>
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50">+ New Workspace</button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Task</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Workspaces</div>
              <div className="text-2xl font-bold">{statistics.total_workspaces}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Team Members</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_members}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Tasks</div>
              <div className="text-2xl font-bold">{statistics.total_tasks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Tasks</div>
              <div className="text-2xl font-bold text-orange-600">{statistics.open_tasks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Overdue</div>
              <div className="text-2xl font-bold text-red-600">{statistics.overdue_tasks}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {workspaces.map(ws => (
            <div key={ws.id} className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md ${selectedWorkspace?.id === ws.id ? 'ring-2 ring-blue-500' : ''}`}
                 onClick={() => setSelectedWorkspace(ws)}>
              <div className="font-semibold mb-1">{ws.name}</div>
              <div className="text-sm text-gray-500 mb-2">{ws.description}</div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>{ws.members_count} members</span>
                <span>{ws.channels_count} channels</span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-semibold">Tasks</h2>
            </div>
            <div className="divide-y">
              {tasks.map(task => (
                <div key={task.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${getPriorityColor(task.priority)}`}>●</span>
                        <span className="font-semibold">{task.title}</span>
                      </div>
                      <div className="text-sm text-gray-500">{task.description}</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(task.status)}`}>
                      {task.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <div className="text-gray-500">
                      <span>Assigned to: {task.assignee.split('@')[0]}</span>
                      <span className="mx-2">•</span>
                      <span>{task.comments_count} comments</span>
                    </div>
                    <div className={`${new Date(task.due_date) < new Date() && task.status !== 'done' ? 'text-red-600' : 'text-gray-500'}`}>
                      Due: {new Date(task.due_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedWorkspace && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">{selectedWorkspace.name}</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Owner</div>
                  <div className="font-medium">{selectedWorkspace.owner}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Description</div>
                  <div>{selectedWorkspace.description}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded">
                    <div className="text-2xl font-bold text-blue-600">{selectedWorkspace.members_count}</div>
                    <div className="text-sm text-gray-500">Members</div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded">
                    <div className="text-2xl font-bold text-purple-600">{selectedWorkspace.channels_count}</div>
                    <div className="text-sm text-gray-500">Channels</div>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  Last activity: {new Date(selectedWorkspace.last_activity).toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
